import * as React from "react";
import mixpanel from "mixpanel-browser";
import { useLocation } from "react-router-dom";
import type { User } from "features/auth";
import { isDev, constants } from "config";

// Configure mixpanel client
mixpanel.init(constants.mixPanelToken, {
  // Ignore DNT or we won't capture any events
  ignore_dnt: true,
  // Prefer localStorage to cookies so we don't hit size limits
  persistence: "localStorage",
  // Turn on debug mode in development
  debug: isDev,
});

/**
 * The method used to Sign In or Sign Up
 */
type AuthMethod = "Wallet" | "Email" | "Magic Link";

/**
 * Tracked button clicks
 */
type ButtonEvent =
  | "Artist Collection"
  | "Connect Wallet"
  | "Download"
  | "Open Call Submit"
  | "Open Call Submit Edit"
  | "Open Call Vote"
  | "Post Link"
  | "Sign In"
  | "Sign Up"
  | "Social Link";

/**
 * Tracked form submissions
 */
type FormEvent =
  | "Email Sign In"
  | "Email Sign Up"
  | "Artist Application"
  | "Open Call Submission"
  | "Open Call Submission Edit"
  | "Open Call Vote";

type MixPanelEventProperties = {
  "Sign In": { Method: AuthMethod; Location?: string };
  "Sign Up": { Method: AuthMethod };
  "Button Click": {
    ButtonName: ButtonEvent;
    ButtonContext: string;
    Location: string;
    Resource?: string;
    ButtonDestination?: string;
  };
  "Form Submit": {
    FormName: FormEvent;
    FormContext?: string;
    ArtistId?: string;
    ApplicationId?: string;
    OpenCallId?: string;
  };
};

type MixPanelEvent = keyof MixPanelEventProperties;

/**
 * Track a user event in MixPanel
 */
const trackEvent = <T extends MixPanelEvent>(
  event: T,
  options: MixPanelEventProperties[T]
) => {
  mixpanel.track(event, options);
};

/**
 * Set User for Mix Panel tracking session
 */
const setUser = (user: User) => {
  mixpanel.identify(user.id);

  mixpanel.people.set({
    user_id: user.id,
    name: user.name,
    email: user.email,
    HasArtistProfile: `${!!user?.claimedArtistPageIds?.length}`,
    // IsHugAdmin: isAdmin ? "true" : "false",
    // HasHugNft: isHugger ? "true" : "false",
  });
};

/**
 * Remove user from Mix Panel tracking session
 *
 * NOTE: don't call too frequently, see:
 * https://docs.mixpanel.com/docs/tracking/reference/javascript#call-reset-at-logout
 */
const removeUser = () => {
  mixpanel.reset();
};

/**
 * Log page view in MixPanel
 *
 * Without this hook in the Router, MixPanel would only log initial load of SPA, not client routing
 */
const usePageTracking = () => {
  const location = useLocation();

  React.useEffect(() => {
    mixpanel.track_pageview(location);
  }, [location]);
};

export { setUser, removeUser, trackEvent, usePageTracking };
